/** @module components/product-feed-layout */
import baustein from "baustein";
import analytics from "web/script/analytics/analytics";
import rothko from "web/script/modules/rothko";
import globals from "web/script/modules/globals";
import ProductFeed, {
    productFeedLoadMoreContent,
    productFeedLoadPreviousContent,
} from "web/script/components/product-feed";
import store from "web/redux/store";
import {
    setButtonUrl,
    setLoading as showPreviousSetLoading,
} from "web/redux/ducks/feed-show-previous";
import { setAll as showMoreSetAll } from "web/redux/ducks/feed-show-more";
import { setAll as feedCountSetAll } from "web/redux/ducks/feed-count";

import { setLoading as sideBarSetLoading } from "web/redux/ducks/feed-sidebar";
import { watchState } from "web/redux/state-watcher";

import "web/script/components/adsense-custom-search-ads";
import "web/script/components/featured-banners-carousel";
import "web/script/components/feed-title";
import "web/script/components/follow-button";
import "web/script/components/product-feed-count";
import "web/script/components/product-page-suggestions";
import "web/script/components/read-more-text";
import "web/script/components/tracked-link";

const LANDING_CONTENT_SELECTOR = ".landing-content";

function showLandingContent(state) {
    return state.feedFiltersReducer.showLandingContent;
}

/**
 * The product feed layout controls the dynamic interactions between the Product Feed,
 * Filters and other components. We can't use a single template to define a layout for
 * product feeds, however, since they're different from page to page, so it's up to this
 * component to ensure some sanity.
 * @constructor
 * @alias module:components/product-feed-layout
 * @extends module:components/components
 */

export default baustein.register(
    "product-feed-layout",
    /** @lends module:components/product-feed-layout.prototype */
    {
        _currentRenderedPage: null,
        initialPage: 0,
        renderedPages: null,
        init() {
            this.client = new rothko.Rothko("modules/product_feed");
            this.client.on("data", this._onGetFeedData.bind(this));

            // capture the initial page this loaded on
            let parsedUrl = new URL(globals.window.location.href);
            this._currentRenderedPage = parsedUrl.pathname + parsedUrl.search;
            this.initialPage = this._getInitialPage();
            this.renderedPages = [this.initialPage];

            // TODO: should this (and a constant with the classname) live in its own module?
            watchState(showLandingContent, (show) => {
                if (show) {
                    return;
                }
                // because ie11 this can't just be `el.remove()` https://caniuse.com/#search=remove
                this.el
                    .querySelectorAll(LANDING_CONTENT_SELECTOR)
                    .forEach((el) => el.parentNode.removeChild(el));
            });

            // This setting prevents default behaviour of scrolling the document when a popstate event occurs
            // Not supported by IE
            if (globals.window.history.scrollRestoration) {
                globals.window.history.scrollRestoration = "manual";
            }
        },
        // Triggered from a simplified history router in lyst-app
        /** @param {import("web/script/utils/history").BrowserHistoryEvent} data */
        async onFilterUpdate(event, data) {
            let { newURL, oldURL, queryChanges } = data;
            let newPageNumber = this._getPageParam(newURL);

            const samePath = newURL.pathname === oldURL.pathname;
            const isOnlyPageChange = queryChanges.length == 1 && queryChanges[0].key == "page";
            if (samePath && isOnlyPageChange) {
                // If a page is already rendered we can just scroll to it
                if (this._pageAlreadyRendered(newPageNumber)) {
                    const existingFeed = this.findComponent("product-feed");
                    existingFeed.scrollToPage(newPageNumber);
                    return Promise.resolve();
                }

                // Append previous set of page results
                if (this._isPreviousPage(newPageNumber)) {
                    this.renderedPages.unshift(newPageNumber);
                    return productFeedLoadPreviousContent(newURL);
                }

                // Append next set of page results
                if (this._isNextPage(newPageNumber)) {
                    this.renderedPages.push(newPageNumber);
                    return productFeedLoadMoreContent(newURL);
                }
            }

            this.initialPage = newPageNumber;
            this.renderedPages = [newPageNumber];
            globals.window.scrollTo(0, 0);

            return this.updateOptions({
                url: newURL.pathname + newURL.search,
            });
        },

        _isNextPage(newPageNumber) {
            let lastRenderedPage = this.renderedPages[this.renderedPages.length - 1];

            if (newPageNumber - lastRenderedPage === 1) {
                return true;
            }

            return false;
        },

        _isPreviousPage(newPageNumber) {
            let lastRenderedPage = this.renderedPages[0];

            if (newPageNumber - lastRenderedPage === -1) {
                return true;
            }

            return false;
        },

        _getInitialPage() {
            return this._getPageParam(window.location.href);
        },

        _getPageParam(pageUrl) {
            let parsedUrl = new URL(pageUrl, globals.window.location.href);
            let page = parseInt(parsedUrl.searchParams.get("page"), 10);
            if (isNaN(page)) {
                return 1;
            }
            return page;
        },

        _pageAlreadyRendered(newPageNumber) {
            return this.renderedPages.includes(newPageNumber) && this.renderedPages.length > 1;
        },

        _onMoreLoaded() {
            const showMoreButton = this.findComponent("click-to-show-oos");
            if (showMoreButton) {
                showMoreButton.destroy();
            }
        },

        /**
         * When the options change into the component, we should load a new feed.
         *
         * Once the feed has been loaded, replace the existing one with the new one. And then
         * load the filters.
         */
        onOptionsChange() {
            const feedUrl = this.options.url;
            if (!feedUrl || this._currentRenderedPage === feedUrl) {
                return;
            }

            this._loadNewFeed(feedUrl);
            this._currentRenderedPage = feedUrl;
        },

        _loadNewFeed(feedUrl) {
            const existingFeed = this.findComponent("product-feed");
            existingFeed.startLoading();
            store.dispatch(sideBarSetLoading(true));

            this.client.fetch({ url: feedUrl }).catch(() => {
                // if an error occurred then reload the page
                globals.window.location.reload();
            });
        },

        _onGetFeedData(ev, data) {
            const existingFeed = this.findComponent("product-feed");

            store.dispatch(
                setButtonUrl(
                    data.show_previous_button ? data.show_previous_button.previous_url : ""
                )
            );
            store.dispatch(showPreviousSetLoading(false));

            if (data.feed_count) {
                store.dispatch(feedCountSetAll(data.feed_count));
            }

            if (data.show_more_button) {
                const showMoreData = data.show_more_button;

                store.dispatch(
                    showMoreSetAll({
                        showMoreNextUrl: showMoreData.show_more_next_url,
                        totalProductCount: showMoreData.total_product_count,
                        visibleProductCount: showMoreData.visible_product_count,
                        showMoreText: showMoreData.show_more_text,
                    })
                );
            }
            // create a new feed and filters components
            const feed = new ProductFeed({
                feedData: data,
                pagination: data.pagination,
                modifier: existingFeed.options.modifier,
                infiniteScrollEnabled: existingFeed.options.infiniteScrollEnabled,
                feedSortDropdown: data.feed_sort_dropdown,
                features: data.features,
            });

            // logging how many rows the user could see, normalised based on desktop
            const itemCount = data.feed_items.length;

            analytics.event("FEED", "POST_FILTER_ROWS", Math.min(Math.ceil(itemCount / 3), 4));

            // insert the new feed, delete the old one
            requestAnimationFrame(() => {
                feed.insertBefore(existingFeed.el);
                existingFeed.destroy();
                store.dispatch(sideBarSetLoading(false));
            });
        },
    }
);
